<template>
  <LoaderSpinner v-if="isRefreshing" />
  <div v-else-if="resources.length > 0">
    <p v-if="totalPosts" class="!text-sm flex justify-between">
      <span v-if="searchQuery.length > 0" class="mb-4">Search results for: <span class="text-primary underline">{{searchQuery}}</span></span>
      Showing {{resources.length}} out of {{totalPosts}} articles
    </p>
    <div class="flex flex-wrap flex-col md:flex-row gap-[3%]">
      <div v-if="activeCategory" class="w-full md:w-[calc(33.333%_-_2%)] mb-4 flex flex-col justify-between">
        <div>
          <h3>{{ activeCategory.name }}</h3>
          <p>{{ activeCategory.description }}</p>
        </div>
        <img v-if="activeCategory?.categoryMeta?.featuredIllustration?.sourceUrl"
             :src="activeCategory.categoryMeta.featuredIllustration.sourceUrl"
             alt=""
             class="max-w-[60%] mb-4" />
      </div>
      <div v-for="resource in resources" :key="resource.id" class="w-full md:w-[calc(33.333%_-_2%)] mb-4">
        <ResourceCard :resource="resource" />
      </div>
    </div>
    <div class="text-center">
      <Button v-if="hasNextPage" @click="handleLoadMore" :is-loading="isLoadingMore">Load more</Button>
    </div>
  </div>
  <div v-else>
    No results found.
  </div>
</template>

<script setup lang="ts">
import { ref, watch, defineProps, defineEmits } from "vue";
import { getPostsQuery } from "~/graphql/queries/getPosts";
import Button from "~/components/global/Buttons/Button.vue";
import ResourceCard from "~/components/pages/blocks/Resources/ResourceCard.vue";

const props = defineProps(["activeCategory", "isRefreshing", "refreshData", "searchQuery"]);
const emit = defineEmits(["refreshStarted", "refreshCompleted"]);

const postCount = ref(5);
const resources = ref([]);
const hasNextPage = ref(false);
const currentPage = ref(1);
const endCursor = ref('');
const isLoadingMore = ref(false);
const totalPosts = ref(null);

const fetchData = async (categoryId: string, end: string = '', searchQuery = '', showTotalCount  = false) => {
  const query = getPostsQuery(postCount.value, false, categoryId, end, '', searchQuery, showTotalCount);
  const { data } = await useAsyncQuery(query);

  if (data.value?.posts?.edges) {
    let resourcesData = data.value?.posts?.edges.map((item: any) => {
      return item.node;
    });

    if (data.value.posts.pageInfo.total) {
      totalPosts.value = data.value.posts.pageInfo.total;
    }

    if (resources.value.length > 0) {
      resources.value.push(...resourcesData)
    } else {
      resources.value = resourcesData;
    }
  }

  if (data.value?.posts?.pageInfo) {
    endCursor.value = data.value.posts.pageInfo?.endCursor;
    hasNextPage.value = data.value.posts.pageInfo?.hasNextPage ?? false;
  }
};

const handleLoadMore = async () => {
  isLoadingMore.value = true;
  currentPage.value = currentPage.value + 1;
  await fetchData(props.activeCategory.categoryId, endCursor.value);
  isLoadingMore.value = false;
};

watch(
  () => props.refreshData,
  async (shouldRefresh) => {
    if (shouldRefresh) {
      emit("refreshStarted", true);
      resources.value = [];
      await fetchData(props.activeCategory.categoryId, '', props.searchQuery, true);
      emit("refreshCompleted", true);
    }
  }
);

watch(
  () => props.searchQuery,
  async (searchQuery) => {
    if (searchQuery) {
      emit("refreshStarted", true);
      resources.value = [];
      await fetchData(props.activeCategory.categoryId, '', searchQuery, true);
      emit("refreshCompleted", true);
    }
  }
);

// Initial data fetch
fetchData(props.activeCategory.categoryId, '', props.searchQuery, true);
</script>
